import { BasicInput } from '@/view/components/BasicInput'
import { Button } from '@/view/components/ui/button'
import { FormProvider, useForm } from 'react-hook-form'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/view/components/ui/alert-dialog'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useState } from 'react'
import { useToast } from '@/view/components/ui/use-toast'
import { Spinner } from '@/view/components/ui/spinner'
import masks, { applyMask } from '@/app/utils/masks'
import { ConfigCard } from './ConfigCard'
import { formatMoneyValue } from '@/app/utils/formatMoneyValue'
import { updateConfig } from '@/app/services/configService/updateConfig'
import { useAuth } from '@/app/hooks/useAuth'
import { BasicSelect } from '@/view/components/BasicSelect'
import { Switch } from '@/view/components/ui/switch'

const unicoValidationSchema = z
  .object({
    UNICO_MIN_SCORE: z.string().min(1, 'Campo obrigatório'),
    UNICO_MAX_RETRY: z.string().min(1, 'Campo obrigatório'),
    UNICO_IS_REQUIRED: z.boolean(),
  })
  .required()

const withdrawalValidationSchema = z
  .object({
    MIN_WITHDRAW_AMOUNT: z.string().min(1, 'Campo obrigatório'),
    WITHDRAW_FEE: z.string().min(1, 'Campo obrigatório'),
    MAX_WITHDRAWALS_PER_MONTH: z.string().min(1, 'Campo obrigatório'),
    WITHDRAW_RESERVE: z.string().min(1, 'Campo obrigatório'),
    ADMIN_WITHDRAW: z.boolean(),
  })
  .required()

const referralValidationSchema = z
  .object({
    REFERRAL_BONUS: z.string().min(1, 'Campo obrigatório'),
    REFERRAL_MODE: z.string().min(1, 'Campo obrigatório'),
  })
  .required()

const supportValidationSchema = z
  .object({
    SUPPORT_NUMBER: z.string().min(1, 'Campo obrigatório'),
    SUPPORT_EMAIL: z
      .string()
      .min(1, 'Campo obrigatório')
      .email('E-mail inválido'),
  })
  .required()

const featuresValidationSchema = z
  .object({
    SHOW_CONTENT: z.boolean(),
    SHOW_PRIZES: z.boolean(),
    SHOW_LINKS: z.boolean(),
    SHOW_PIN: z.boolean(),
  })
  .required()

const actionsValidationSchema = z
  .object({
    CAN_CREATE_SELLER: z.boolean(),
    CAN_REGISTER: z.boolean(),
    CAN_SEND_NOTIFICATION: z.boolean(),
  })
  .required()

const pagesValidationSchema = z
  .object({
    SHOW_LEADS_PAGE: z.boolean(),
    SHOW_RADAR_PAGE: z.boolean(),
    SHOW_MANAGER_PAGE: z.boolean(),
  })
  .required()

const appValidationSchema = z
  .object({
    INSTAGRAM_URL: z.string().url('URL inválida').min(1, 'Campo obrigatório'),
    WEB_URL: z.string().url('URL inválida').min(1, 'Campo obrigatório'),
    TRAINING_URL: z.string().url('URL inválida').min(1, 'Campo obrigatório'),
  })
  .required()

const configsMap: any = {}

interface ModalEditTransactionProps {
  config: Config
  onSubmit: () => void
}

export const ConfigCardWithModal = ({
  config,
  onSubmit,
}: ModalEditTransactionProps) => {
  const [open, setOpen] = useState(false)
  const { toast } = useToast()

  const { isSuperAdmin } = useAuth()

  const handleSubmit = async (formData: any) => {
    try {
      const payload: any = {}

      if (config.type === 'app') {
        payload.INSTAGRAM_URL = formData.INSTAGRAM_URL
        payload.WEB_URL = formData.WEB_URL
        payload.TRAINING_URL = formData.TRAINING_URL
        payload.TEAMS_APP_IOS_URL = config.TEAMS_APP_IOS_URL
        payload.TEAMS_APP_GOOGLE_URL = config.TEAMS_APP_GOOGLE_URL
      } else {
        for (const [key, value] of Object.entries(formData)) {
          if (
            key === 'UNICO_IS_REQUIRED' ||
            key === 'REFERRAL_MODE' ||
            key === 'SHOW_CONTENT' ||
            key === 'SHOW_PRIZES' ||
            key === 'SHOW_LINKS' ||
            key === 'ADMIN_WITHDRAW' ||
            key === 'SHOW_PIN' ||
            key === 'CAN_CREATE_SELLER' ||
            key === 'CAN_REGISTER' ||
            key === 'CAN_SEND_NOTIFICATION' ||
            key === 'SHOW_LEADS_PAGE' ||
            key === 'SHOW_RADAR_PAGE' ||
            key === 'SHOW_MANAGER_PAGE'
          ) {
            payload[key] = value
          } else if (key === 'SUPPORT_NUMBER') {
            payload[key] = masks.number(value)
          } else if (key === 'SUPPORT_EMAIL') {
            payload[key] = value
          } else {
            payload[key] = masks.number(value)
          }
        }
      }

      await updateConfig(config._id, { ...payload, type: config.type })

      onSubmit()
      toast({ title: 'Configuração atualizada com sucesso' })
      setOpen(false)
    } catch (error: any) {
      toast({ title: 'Não foi possível atualizar a configuração' })
    }
  }

  if (isSuperAdmin) {
    configsMap.unico = {
      schema: unicoValidationSchema,
      title: 'Unico',
      items: [
        {
          key: 'UNICO_IS_REQUIRED',
          label: 'Ativar',
          isSwitch: true,
        },
        {
          key: 'UNICO_MIN_SCORE',
          label: 'Score mínimo',
          format: (v: string) => masks.number(v),
        },
        {
          key: 'UNICO_MAX_RETRY',
          label: 'Número de tentativas',
          format: (v: string) => masks.number(v),
        },
      ],
    }

    configsMap.referral = {
      schema: referralValidationSchema,
      title: 'Bônus de Indicação',
      items: [
        {
          key: 'REFERRAL_BONUS',
          format: (v: string) => formatMoneyValue(Number(v)),
          label: 'Valor',
          inputMask: masks.currency,
        },
        {
          key: 'REFERRAL_MODE',
          label: 'Ordenação',
          format: (v: string) => {
            return ReferralModeOptions.find((o) => o.value === v)?.label || v
          },
        },
      ],
    }

    configsMap.withdrawal = {
      schema: withdrawalValidationSchema,
      title: 'Saques',
      items: [
        {
          key: 'WITHDRAW_FEE',
          format: (v: string) => formatMoneyValue(Number(v)),
          label: 'Taxa',
          inputMask: masks.currency,
        },
        {
          key: 'MIN_WITHDRAW_AMOUNT',
          format: (v: string) => formatMoneyValue(Number(v)),
          inputMask: masks.currency,
          label: 'Saque mínimo',
        },
        {
          key: 'MAX_WITHDRAWALS_PER_MONTH',
          label: 'Saques diários',
          inputMask: masks.number,
          format: (v: string) => v,
        },
        {
          key: 'WITHDRAW_RESERVE',
          format: (v: string) => formatMoneyValue(Number(v)),
          label: 'Lastro',
          inputMask: masks.currency,
          readOnly: true,
        },
        {
          key: 'ADMIN_WITHDRAW',
          label: 'Saques do admin',
          isSwitch: true,
          showSwitch: true,
        },
      ],
    }

    configsMap.support = {
      schema: supportValidationSchema,
      title: 'Suporte',
      items: [
        {
          key: 'SUPPORT_NUMBER',
          label: 'Número de telefone',
          format: (v: string) => masks.phone(v),
          inputMask: masks.phone,
        },
        {
          key: 'SUPPORT_EMAIL',
          label: 'Email',
        },
      ],
    }

    configsMap.features = {
      schema: featuresValidationSchema,
      title: 'Funcionalidades',
      items: [
        {
          key: 'SHOW_CONTENT',
          label: 'Conteúdos',
          isSwitch: true,
          showSwitch: true,
        },
        {
          key: 'SHOW_PRIZES',
          label: 'Sorteios',
          isSwitch: true,
          showSwitch: true,
        },
        {
          key: 'SHOW_LINKS',
          label: 'Links',
          isSwitch: true,
          showSwitch: true,
        },
        {
          key: 'SHOW_PIN',
          label: 'PIN',
          isSwitch: true,
          showSwitch: true,
        },
      ],
    }

    configsMap.actions = {
      schema: actionsValidationSchema,
      title: 'Ações',
      items: [
        {
          key: 'CAN_CREATE_SELLER',
          label: 'Criar Vendedor',
          isSwitch: true,
          showSwitch: true,
        },
        {
          key: 'CAN_REGISTER',
          label: 'Cadastro',
          isSwitch: true,
          showSwitch: true,
        },
        {
          key: 'CAN_SEND_NOTIFICATION',
          label: 'Enviar Notificação',
          isSwitch: true,
          showSwitch: true,
        },
      ],
    }

    configsMap.pages = {
      schema: pagesValidationSchema,
      title: 'Visualizações',
      items: [
        {
          key: 'SHOW_LEADS_PAGE',
          label: 'Página de Leads',
          isSwitch: true,
          showSwitch: true,
        },
        {
          key: 'SHOW_RADAR_PAGE',
          label: 'Página Radar',
          isSwitch: true,
          showSwitch: true,
        },
        {
          key: 'SHOW_MANAGER_PAGE',
          label: 'Página de Gerentes',
          isSwitch: true,
          showSwitch: true,
        },
      ],
    }

    configsMap.app = {
      schema: appValidationSchema,
      title: 'Acessos',
      items: [
        {
          key: 'WEB_URL',
          label: 'Portal Web',
        },
        {
          key: 'INSTAGRAM_URL',
          label: 'Instagram',
        },
        {
          key: 'TRAINING_URL',
          label: 'Treinamento',
        },
      ],
    }
  }

  const ReferralModeOptions = [
    { value: 'first', label: 'Primeiro' },
    { value: 'last', label: 'Ultimo' },
  ]
  const selectedConfig = configsMap[config.type as keyof typeof configsMap]
  const title = selectedConfig?.title
  const configItems = selectedConfig?.items
  const schema = selectedConfig?.schema

  const form = useForm<any>({
    resolver: zodResolver(schema),
    defaultValues: {
      UNICO_MIN_SCORE:
        config.type === 'unico' ? String(config.UNICO_MIN_SCORE || '') : '',
      UNICO_MAX_RETRY:
        config.type === 'unico' ? String(config.UNICO_MAX_RETRY || '') : '',
      UNICO_IS_REQUIRED:
        config.type === 'unico' ? Boolean(config.UNICO_IS_REQUIRED) : false,

      REFERRAL_BONUS:
        config.type === 'referral'
          ? formatMoneyValue(Number(config.REFERRAL_BONUS || 0))
          : '',
      REFERRAL_MODE:
        config.type === 'referral' ? String(config.REFERRAL_MODE || '') : '',

      MIN_WITHDRAW_AMOUNT:
        config.type === 'withdrawal'
          ? formatMoneyValue(Number(config.MIN_WITHDRAW_AMOUNT || 0))
          : '',
      WITHDRAW_FEE:
        config.type === 'withdrawal'
          ? formatMoneyValue(Number(config.WITHDRAW_FEE || 0))
          : '',
      MAX_WITHDRAWALS_PER_MONTH:
        config.type === 'withdrawal'
          ? String(config.MAX_WITHDRAWALS_PER_MONTH || '')
          : '',
      WITHDRAW_RESERVE:
        config.type === 'withdrawal'
          ? formatMoneyValue(Number(config.WITHDRAW_RESERVE || 0))
          : '',
      ADMIN_WITHDRAW:
        config.type === 'withdrawal'
          ? Boolean(config.ADMIN_WITHDRAW) : false,

      SUPPORT_NUMBER:
        config.type === 'support'
          ? masks.phone(String(config.SUPPORT_NUMBER || ''))
          : '',
      SUPPORT_EMAIL:
        config.type === 'support' ? String(config.SUPPORT_EMAIL || '') : '',

      SHOW_CONTENT:
        config.type === 'features' ? Boolean(config.SHOW_CONTENT) : false,
      SHOW_PRIZES:
        config.type === 'features' ? Boolean(config.SHOW_PRIZES) : false,
      SHOW_LINKS:
        config.type === 'features' ? Boolean(config.SHOW_LINKS) : false,
      SHOW_PIN:
        config.type === 'features' ? Boolean(config.SHOW_PIN) : false,

      CAN_CREATE_SELLER:
        config.type === 'actions' ? Boolean(config.CAN_CREATE_SELLER) : false,
      CAN_REGISTER:
        config.type === 'actions' ? Boolean(config.CAN_REGISTER) : false,
      CAN_SEND_NOTIFICATION:
        config.type === 'actions' ? Boolean(config.CAN_SEND_NOTIFICATION) : false,

      SHOW_LEADS_PAGE:
        config.type === 'pages' ? Boolean(config.SHOW_LEADS_PAGE) : false,
      SHOW_RADAR_PAGE:
        config.type === 'pages' ? Boolean(config.SHOW_RADAR_PAGE) : false,
      SHOW_MANAGER_PAGE:
        config.type === 'pages' ? Boolean(config.SHOW_MANAGER_PAGE) : false,

      INSTAGRAM_URL:
        config.type === 'app' ? String(config.INSTAGRAM_URL || '') : '',
      WEB_URL: config.type === 'app' ? String(config.WEB_URL || '') : '',
      TRAINING_URL:
        config.type === 'app' ? String(config.TRAINING_URL || '') : '',
    },
  })
  if (!configItems) return null

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <div className="flex h-full w-full items-center justify-between">
          <ConfigCard
            updatedAt={config.updatedAt}
            title={title}
            showSwitch={config.type === 'unico'}
            switchActive={
              config.type === 'unico'
                ? Boolean(config.UNICO_IS_REQUIRED)
                : false
            }
            items={configItems
              .filter((item: any) =>
                config.type === 'features' || config.type === 'actions' || config.type === 'pages'
                  ? true
                  : !item.isSwitch,
              )
              .slice(0, 3)
              .map((item: any) => ({
                label: String(item.label),
                value: item.showSwitch
                  ? Boolean(config[item.key as keyof Config])
                  : item.format
                    ? item.format(String(config[item.key as keyof Config]))
                    : String(config[item.key as keyof Config]),
                isSwitch: item.showSwitch || false,
              }))}
            isAppConfig={config.type === 'app'}
          />
        </div>
      </AlertDialogTrigger>

      <AlertDialogContent className="sm:max-w-md">
        <form onSubmit={form.handleSubmit(handleSubmit, (e) => console.log(e))}>
          <FormProvider {...form}>
            <AlertDialogHeader className="border-b pb-4">
              <AlertDialogTitle className="text-xl font-semibold text-black01">
                {title}
              </AlertDialogTitle>
            </AlertDialogHeader>

            <AlertDialogDescription className="flex flex-col gap-4 py-4 text-base text-black04">
              {configItems.map((item: any) => {
                if (item.key === 'REFERRAL_MODE')
                  return (
                    <BasicSelect
                      key={item.key}
                      name={item.key}
                      label={item.label}
                      options={ReferralModeOptions}
                      placeholder=""
                      error={
                        (form.formState.errors as any)[item.key]?.message
                      }
                    />
                  )

                if (item.isSwitch)
                  return (
                    <div
                      key={item.key}
                      className="flex items-center justify-between"
                    >
                      <label className="text-sm font-medium">
                        {item.label}
                      </label>
                      <Switch
                        checked={form.watch(item.key)}
                        onCheckedChange={(checked) =>
                          form.setValue(item.key, checked)
                        }
                        className="data-[state=checked]:bg-primary"
                      />
                    </div>
                  )

                return (
                  <BasicInput
                    key={item.key}
                    name={item.key}
                    label={item.label}
                    readOnly={item.readOnly}
                    onInput={
                      item.inputMask
                        ? applyMask(item.inputMask, item.inputMask)
                        : item.format
                          ? applyMask(item.format, masks.number)
                          : undefined
                    }
                    defaultValue={
                      item.inputMask && item.key === 'SUPPORT_NUMBER'
                        ? masks.phone(
                          String(config[item.key as keyof Config]),
                        )
                        : item.inputMask === masks.currency
                          ? formatMoneyValue(
                            Number(config[item.key as keyof Config]),
                          )
                          : item.format
                            ? item.format(
                              String(config[item.key as keyof Config]),
                            )
                            : String(config[item.key as keyof Config])
                    }
                    error={
                      (form.formState.errors as any)[item.key]?.message
                    }
                  />
                )
              })}
            </AlertDialogDescription>

            <AlertDialogFooter>
              <AlertDialogCancel asChild>
                <Button variant="outline" className="bg-gray02 text-primary hover:bg-gray01" type="button">
                  Cancelar
                </Button>
              </AlertDialogCancel>
              <Button
                type="submit"
                disabled={form.formState.isSubmitting}
                className="text-white"
              >
                {form.formState.isSubmitting ? (
                  <Spinner className="text-white" />
                ) : (
                  'Confirmar'
                )}
              </Button>
            </AlertDialogFooter>
          </FormProvider>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  )
}
